/* GENERATED FILE — do not edit.
   Source: shared-ui/theme/useTheme.js
   Run "node shared-ui/sync.js" after editing the source. */
import { useCallback, useEffect, useState } from 'react';

const DEFAULT_THEME = 'light';

/* Each app owns its own preference, so the key comes from the app, not from here.
   The no-flash script in public/index.html stamps it on <html> before first paint:
     document.documentElement.dataset.themeKey = 'admin-theme'
   Falling back keeps a single-app setup working with no wiring at all. */
function storageKey() {
  try {
    return document.documentElement.dataset.themeKey || 'app-theme';
  } catch (e) {
    return 'app-theme';
  }
}

function readStored() {
  try {
    const saved = localStorage.getItem(storageKey());
    if (saved === 'dark' || saved === 'light') return saved;
  } catch (e) {
    /* storage blocked (private mode) — fall back to system */
  }
  return null;
}

function writeStored(value) {
  try {
    localStorage.setItem(storageKey(), value);
  } catch (e) {
    /* not persisted — the attribute still applies for this session */
  }
}

function systemPrefersDark() {
  return !!(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
}

/* Apply the theme as early as possible so the first paint is right.
   Light is the product default: with nothing stored we stamp 'light' rather than
   following the OS, so a scout on a dark-mode machine still lands on the light UI. */
function applyStoredOnce() {
  const root = document.documentElement;
  const saved = readStored() || DEFAULT_THEME;
  root.setAttribute('data-theme', saved);
  return saved;
}

export default function useTheme() {
  const [theme, setTheme] = useState(() => {
    const attr = document.documentElement.getAttribute('data-theme');
    if (attr === 'dark' || attr === 'light') return attr;
    return readStored() || DEFAULT_THEME;
  });

  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  // First load: apply a saved value if present, otherwise leave the attribute unset.
  useEffect(() => {
    const saved = applyStoredOnce();
    setTheme(saved);
  }, []);

  // Track the system preference so isDark stays correct while theme is null.
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (ev) => setSystemDark(ev.matches);

    if (mq.addEventListener) {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    if (mq.addListener) {
      mq.addListener(onChange);
      return () => mq.removeListener(onChange);
    }
    return undefined;
  }, []);

  const isDark = theme === 'dark' || (theme === null && systemDark);

  const toggle = useCallback(() => {
    const root = document.documentElement;
    const attr = root.getAttribute('data-theme');
    const currentlyDark =
      attr === 'dark' ? true : attr === 'light' ? false : systemPrefersDark();
    const next = currentlyDark ? 'light' : 'dark';

    root.setAttribute('data-theme', next);
    writeStored(next);
    setTheme(next);
  }, []);

  return { theme, isDark, toggle };
}
